import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Thermometer, Wind, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, CloudSun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TemperatureTechDashboard = ({ user, onLogout }) => {
  const [rooms, setRooms] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [currentShift, setCurrentShift] = useState('morning');
  const [completedRooms, setCompletedRooms] = useState(new Set());
  const [partialRooms, setPartialRooms] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(0);
  const [roomData, setRoomData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    let roomsData = [];
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, statusRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/temperature-logs/rooms-status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      roomsData = roomsRes.data || [];
      setRooms(roomsData);
      
      // Calculate morning/evening completed counts from rooms data
      const statusRooms = statusRes.data.rooms || [];
      const morningCompleted = statusRooms.filter(r => r.morning_logged).length;
      const eveningCompleted = statusRooms.filter(r => r.evening_logged).length;
      
      // Update todayStatus with calculated counts
      setTodayStatus({
        ...statusRes.data,
        morning_completed: morningCompleted,
        evening_completed: eveningCompleted,
        total_rooms: statusRooms.length
      });
      
      // Single source of truth: derive today's logged/complete/partial rooms for
      // the current shift from rooms-status (correctly filtered to today IST).
      const completed = new Set();   // logged AND all readings captured
      const partial = new Set();     // logged but missing some readings
      statusRooms.forEach(r => {
        const loggedToday = currentShift === 'morning' ? r.morning_logged : r.evening_logged;
        const completeToday = currentShift === 'morning' ? r.morning_complete : r.evening_complete;
        if (loggedToday) {
          if (completeToday) completed.add(r.room_id);
          else partial.add(r.room_id);
        }
      });
      setCompletedRooms(completed);
      setPartialRooms(partial);

      // Initialize room data for all rooms
      // For paired rooms (names with &), use 2 air temps + 2 CO2; for single rooms, use 1 of each
      const initialData = {};
      roomsData.forEach(room => {
        const isPairedRoom = room.name && room.name.includes('&');
        initialData[room.id] = {
          room_id: room.id,
          room_name: room.name,
          is_paired: isPairedRoom,
          air_temperature_1: '',
          air_temperature_2: isPairedRoom ? '' : null, // Only for paired rooms
          compost_readings: Array(18).fill(null).map((_, i) => ({ reading_number: i + 1, temperature: '' })),
          co2_level_1: '',
          co2_level_2: isPairedRoom ? '' : null, // Only for paired rooms
          notes: ''
        };
      });
      setRoomData(initialData);
    } catch (error) {
      // Only show toast if we couldn't get any rooms
      if (roomsData.length === 0) {
        toast.error('Failed to fetch room data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentShift]);

  /**
   * Auto-insert a decimal so the temp tech can type "203" and get "20.3".
   * Rule: if the value is purely 3+ digits with no decimal AND ≥ 100,
   * split as "ab.c" (last digit becomes the fractional). Two-digit values
   * (e.g. "21") are left untouched so the user can keep typing.
   * Treats empty string and values containing "." as user-controlled.
   */
  const autoDecimal = (raw) => {
    if (raw == null || raw === '') return raw;
    const str = String(raw);
    if (str.includes('.') || str.startsWith('-')) return str;
    if (!/^\d+$/.test(str)) return str;
    if (str.length < 3) return str;
    // Insert "." before last digit: "203" -> "20.3", "2050" -> "205.0"
    return `${str.slice(0, -1)}.${str.slice(-1)}`;
  };

  const handleAirTempChange = (roomId, value, inputNum = 1) => {
    setRoomData(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [`air_temperature_${inputNum}`]: autoDecimal(value)
      }
    }));
  };

  const handleCompostReadingChange = (roomId, readingIndex, value) => {
    setRoomData(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        compost_readings: prev[roomId].compost_readings.map((r, i) =>
          i === readingIndex ? { ...r, temperature: autoDecimal(value) } : r
        )
      }
    }));
  };

  const handleCO2Change = (roomId, value, inputNum = 1) => {
    setRoomData(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [`co2_level_${inputNum}`]: value
      }
    }));
  };

  const validateRoomData = (roomId) => {
    const data = roomData[roomId];
    if (!data) return false;
    if (!Array.isArray(data.compost_readings)) return false;

    // Check air temperature(s)
    const airTemp1Filled = data.air_temperature_1 !== '' && data.air_temperature_1 !== null && data.air_temperature_1 !== undefined;
    const airTemp2Filled = data.is_paired
      ? (data.air_temperature_2 !== '' && data.air_temperature_2 !== null && data.air_temperature_2 !== undefined)
      : true;

    const allCompostReadingsFilled = data.compost_readings.every(r => r && r.temperature !== '' && r.temperature !== null && r.temperature !== undefined);

    // Check CO2(s)
    const co2_1Filled = data.co2_level_1 !== '' && data.co2_level_1 !== null && data.co2_level_1 !== undefined;
    const co2_2Filled = data.is_paired
      ? (data.co2_level_2 !== '' && data.co2_level_2 !== null && data.co2_level_2 !== undefined)
      : true;

    return airTemp1Filled && airTemp2Filled && allCompostReadingsFilled && co2_1Filled && co2_2Filled;
  };

  // Count of compost readings the tech has actually filled in.
  const filledCompostCount = (roomId) => {
    const data = roomData[roomId];
    if (!data || !Array.isArray(data.compost_readings)) return 0;
    return data.compost_readings.filter(r => r && r.temperature !== '' && r.temperature !== null && r.temperature !== undefined).length;
  };

  // Minimum to allow a (possibly partial) submission: at least one reading of any kind.
  const canSubmitRoom = (roomId) => {
    const data = roomData[roomId];
    if (!data) return false;
    const airFilled = (data.air_temperature_1 !== '' && data.air_temperature_1 != null) ||
      (data.air_temperature_2 !== '' && data.air_temperature_2 != null);
    const co2Filled = (data.co2_level_1 !== '' && data.co2_level_1 != null) ||
      (data.co2_level_2 !== '' && data.co2_level_2 != null);
    return airFilled || co2Filled || filledCompostCount(roomId) > 0;
  };

  const handleSubmitRoom = async (roomId) => {
    if (!canSubmitRoom(roomId)) {
      toast.error('Enter at least one reading (air temp, a compost reading, or CO2) before submitting');
      return;
    }

    const data = roomData[roomId];
    const isComplete = validateRoomData(roomId);
    // Warn (but still allow) when submitting an incomplete reading.
    if (!isComplete) {
      const count = filledCompostCount(roomId);
      const ok = window.confirm(
        `This is a partial reading (${count}/18 compost readings filled). Submit anyway? It will be marked "Partial".`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      const num = (v) => (v === '' || v == null ? null : parseFloat(v));
      const air1 = num(data.air_temperature_1);
      const air2 = data.is_paired ? num(data.air_temperature_2) : null;
      const co2_1 = num(data.co2_level_1);
      const co2_2 = data.is_paired ? num(data.co2_level_2) : null;

      // Average across whichever values are present (partial-safe)
      const avgOf = (...vals) => {
        const present = vals.filter(v => v != null && !isNaN(v));
        return present.length ? present.reduce((a, b) => a + b, 0) / present.length : null;
      };
      const avgAirTemp = avgOf(air1, ...(data.is_paired ? [air2] : []));
      const avgCo2 = avgOf(co2_1, ...(data.is_paired ? [co2_2] : []));

      // Only send compost readings that were actually filled
      const compostReadings = data.compost_readings
        .filter(r => r && r.temperature !== '' && r.temperature != null)
        .map(r => ({ reading_number: r.reading_number, temperature: parseFloat(r.temperature) }));

      await axios.post(`${API}/temperature-logs`, {
        room_id: roomId,
        shift: currentShift,
        air_temperature: avgAirTemp,
        air_temperature_1: air1,
        air_temperature_2: air2,
        is_paired_room: data.is_paired || false,
        compost_readings: compostReadings,
        co2_level: avgCo2,
        co2_level_1: co2_1,
        co2_level_2: co2_2,
        notes: data.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${rooms[selectedRoomIndex]?.name} ${isComplete ? 'readings submitted!' : 'partial reading saved'}`);

      // Mark as completed/partial locally and move to next room
      if (isComplete) {
        setCompletedRooms(prev => new Set([...prev, roomId]));
        setPartialRooms(prev => { const n = new Set(prev); n.delete(roomId); return n; });
      } else {
        setPartialRooms(prev => new Set([...prev, roomId]));
      }

      // Reset this room's data
      const isPairedRoom = rooms[selectedRoomIndex]?.name?.includes('&');
      setRoomData(prev => ({
        ...prev,
        [roomId]: {
          room_id: roomId,
          room_name: rooms[selectedRoomIndex]?.name,
          is_paired: isPairedRoom,
          air_temperature_1: '',
          air_temperature_2: isPairedRoom ? '' : null,
          compost_readings: Array(18).fill(null).map((_, i) => ({ reading_number: i + 1, temperature: '' })),
          co2_level_1: '',
          co2_level_2: isPairedRoom ? '' : null,
          notes: ''
        }
      }));

      // Move to next incomplete room
      const nextIncompleteIndex = rooms.findIndex((room, index) => 
        index > selectedRoomIndex && !completedRooms.has(room.id) && room.id !== roomId
      );
      if (nextIncompleteIndex !== -1) {
        setSelectedRoomIndex(nextIncompleteIndex);
      }

      // Refresh status
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit readings');
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextRoom = () => {
    if (selectedRoomIndex < rooms.length - 1) {
      setSelectedRoomIndex(selectedRoomIndex + 1);
    }
  };

  const goToPrevRoom = () => {
    if (selectedRoomIndex > 0) {
      setSelectedRoomIndex(selectedRoomIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9FAF9]">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  const currentRoom = rooms[selectedRoomIndex];
  const isCurrentRoomCompleted = currentRoom && completedRooms.has(currentRoom.id);
  const isCurrentRoomPartial = currentRoom && partialRooms.has(currentRoom.id);
  const completedCount = rooms.filter(r => completedRooms.has(r.id)).length;
  const partialCount = rooms.filter(r => partialRooms.has(r.id)).length;

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Temperature Monitoring</h1>
              <p className="text-sm text-gray-500">1-2 Air Temps + 18 Compost Temps + 1-2 CO2</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user.full_name}</span>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Today's Status */}
        {todayStatus && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50" data-testid="today-status-card">
            <CardHeader>
              <CardTitle className="text-lg">Today's Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  {todayStatus.morning_completed === todayStatus.total_rooms ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Morning Shift</p>
                    <p className="text-xl font-bold">{todayStatus.morning_completed}/{todayStatus.total_rooms}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {todayStatus.evening_completed === todayStatus.total_rooms ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Evening Shift</p>
                    <p className="text-xl font-bold">{todayStatus.evening_completed}/{todayStatus.total_rooms}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shift Selection */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Label className="text-base font-medium mb-3 block">Select Shift</Label>
            <Tabs value={currentShift} onValueChange={setCurrentShift}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="morning" data-testid="morning-shift-tab">Morning Shift</TabsTrigger>
                <TabsTrigger value="evening" data-testid="evening-shift-tab">Evening Shift</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        {/* Room Selection - Mini Overview */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-1">
              <span>Select Room ({completedCount}/{rooms.length} done{partialCount > 0 ? `, ${partialCount} partial` : ''})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2">
              {rooms.map((room, index) => {
                const isCompleted = completedRooms.has(room.id);
                const isPartial = partialRooms.has(room.id);
                const isSelected = index === selectedRoomIndex;
                const isPaired = room.name && room.name.includes('&');
                return (
                  <button
                    key={room.id}
                    onClick={() => setSelectedRoomIndex(index)}
                    className={`p-2 rounded-lg text-xs font-medium transition-all ${
                      isSelected 
                        ? 'bg-[#1A4D2E] text-white ring-2 ring-[#1A4D2E] ring-offset-2' 
                        : isCompleted 
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : isPartial
                            ? 'bg-amber-100 text-amber-800 border border-amber-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`room-selector-${room.room_number}`}
                  >
                    {room.name}
                    {isCompleted && <CheckCircle className="w-3 h-3 inline ml-1" />}
                    {!isCompleted && isPartial && <AlertCircle className="w-3 h-3 inline ml-1" />}
                    {isPaired && <span className="block text-[10px] opacity-75">(2 air temps)</span>}
                    {!isCompleted && isPartial && <span className="block text-[10px] opacity-90 font-semibold">Partial</span>}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Current Room Form */}
        {currentRoom && (
          <Card className="border-gray-200" data-testid={`room-form-${currentRoom.room_number}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Thermometer className="w-6 h-6 text-[#1A4D2E]" />
                  {currentRoom.name}
                  {isCurrentRoomCompleted && (
                    <Badge className="bg-green-100 text-green-800 ml-2">Completed</Badge>
                  )}
                  {!isCurrentRoomCompleted && isCurrentRoomPartial && (
                    <Badge className="bg-amber-100 text-amber-800 ml-2">Partial — add remaining readings</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPrevRoom}
                    disabled={selectedRoomIndex === 0}
                    data-testid="prev-room-button"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600 min-w-[60px] text-center">
                    {selectedRoomIndex + 1} / {rooms.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextRoom}
                    disabled={selectedRoomIndex === rooms.length - 1}
                    data-testid="next-room-button"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isCurrentRoomCompleted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <p className="text-lg font-medium text-green-800">This room has been logged for {currentShift} shift</p>
                  <p className="text-sm text-gray-600 mt-2">Select another room or switch shift to continue</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Air Temperature */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <CloudSun className="w-4 h-4 text-blue-600" />
                      Room Air Temperature (°C)
                      {roomData[currentRoom.id]?.is_paired && (
                        <span className="text-xs text-blue-600 ml-2">(2 readings required for paired rooms)</span>
                      )}
                    </Label>
                    
                    {roomData[currentRoom.id]?.is_paired ? (
                      // Paired room - show 2 air temperature inputs
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            {currentRoom.name.split('&')[0].trim()} Air Temp
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Enter air temperature"
                            value={roomData[currentRoom.id]?.air_temperature_1 || ''}
                            onChange={(e) => handleAirTempChange(currentRoom.id, e.target.value, 1)}
                            className="bg-white"
                            data-testid={`air-temp-1-${currentRoom.room_number}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            ROOM {currentRoom.name.split('&')[1].trim()} Air Temp
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Enter air temperature"
                            value={roomData[currentRoom.id]?.air_temperature_2 || ''}
                            onChange={(e) => handleAirTempChange(currentRoom.id, e.target.value, 2)}
                            className="bg-white"
                            data-testid={`air-temp-2-${currentRoom.room_number}`}
                          />
                        </div>
                      </div>
                    ) : (
                      // Single room - show 1 air temperature input
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Enter air temperature"
                        value={roomData[currentRoom.id]?.air_temperature_1 || ''}
                        onChange={(e) => handleAirTempChange(currentRoom.id, e.target.value, 1)}
                        className="max-w-xs bg-white"
                        data-testid={`air-temp-${currentRoom.room_number}`}
                      />
                    )}
                  </div>

                  {/* Compost Temperature Readings Grid (up to 18, partial allowed) */}
                  <div>
                    <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                      Compost Temperature Readings (°C)
                      <span className="text-xs font-normal text-gray-500">
                        {filledCompostCount(currentRoom.id)}/18 filled — partial is okay
                      </span>
                    </Label>
                    <div className="grid grid-cols-4 gap-3">
                      {(roomData[currentRoom.id]?.compost_readings || Array(18).fill(null).map((_, i) => ({ reading_number: i + 1, temperature: '' }))).map((reading, index) => (
                        <div key={index} className="flex flex-col">
                          <label className="text-xs text-gray-500 mb-1 text-center">#{index + 1}</label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="50"
                            placeholder="--"
                            value={reading?.temperature || ''}
                            onChange={(e) => handleCompostReadingChange(currentRoom.id, index, e.target.value)}
                            className="text-center"
                            data-testid={`compost-temp-${currentRoom.room_number}-${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CO2 Reading(s) */}
                  <div>
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Wind className="w-4 h-4" />
                      CO2 Level (ppm)
                      {roomData[currentRoom.id]?.is_paired && (
                        <span className="text-xs text-blue-600 ml-2">(2 readings required for paired rooms)</span>
                      )}
                    </Label>

                    {roomData[currentRoom.id]?.is_paired ? (
                      // Paired room - 2 CO2 inputs
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            {currentRoom.name.split('&')[0].trim()} CO2
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Enter CO2 level"
                            value={roomData[currentRoom.id]?.co2_level_1 || ''}
                            onChange={(e) => handleCO2Change(currentRoom.id, e.target.value, 1)}
                            data-testid={`co2-1-${currentRoom.room_number}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            ROOM {currentRoom.name.split('&')[1].trim()} CO2
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Enter CO2 level"
                            value={roomData[currentRoom.id]?.co2_level_2 || ''}
                            onChange={(e) => handleCO2Change(currentRoom.id, e.target.value, 2)}
                            data-testid={`co2-2-${currentRoom.room_number}`}
                          />
                        </div>
                      </div>
                    ) : (
                      // Single room - 1 CO2 input
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Enter CO2 level"
                            value={roomData[currentRoom.id]?.co2_level_1 || ''}
                            onChange={(e) => handleCO2Change(currentRoom.id, e.target.value, 1)}
                            data-testid={`co2-${currentRoom.room_number}`}
                          />
                        </div>
                        {validateRoomData(currentRoom.id) && (
                          <div className="flex items-center gap-2 text-green-600 pb-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">Ready to submit</span>
                          </div>
                        )}
                      </div>
                    )}

                    {roomData[currentRoom.id]?.is_paired && validateRoomData(currentRoom.id) && (
                      <div className="flex items-center gap-2 text-green-600 mt-3">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">Ready to submit</span>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    data-testid="submit-room-button"
                    onClick={() => handleSubmitRoom(currentRoom.id)}
                    disabled={submitting || !canSubmitRoom(currentRoom.id)}
                    className="w-full bg-[#1A4D2E] hover:bg-[#143D23] text-white text-lg py-6"
                    size="lg"
                  >
                    {submitting
                      ? 'Submitting...'
                      : validateRoomData(currentRoom.id)
                        ? `Submit ${currentRoom.name} - ${currentShift.charAt(0).toUpperCase() + currentShift.slice(1)} Shift`
                        : `Submit Partial (${filledCompostCount(currentRoom.id)}/18) - ${currentRoom.name}`}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default TemperatureTechDashboard;
